import React, { useContext, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';

import ReportContext from 'contexts/ReportContext';
import AccountPickerContext from 'contexts/AccountPickerContext';
import STORE from 'store';
import isDefined from 'isDefined';
import withoutNulls from 'api/expression/withoutNulls';
import QuickFilters from '../../CustomerLaneCommitsBoard/Settings/QuickFilters/QuickFilters';
import NewReportQuickFilterForm from './NewReportQuickFilterForm';

const ReportQuickFiltersSettings = ({ close }: { close: () => void }) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { report, quickFilters } = useContext(ReportContext);
  const quickFilterOrder = report?.quickFilterOrder || [];
  const [isCreatingNewFilter, setIsCreatingNewFilter] =
    useState<boolean>(false);
  const [editingFilter, setEditingFilter] = useState<
    LegacySavedFilter | undefined
  >(undefined);

  // quickFilterOrder may reference IDs for filters that have since been deleted.
  // Filter those out, then append any filters missing from the order array at the end.
  const orderedFilters = quickFilterOrder
    .map((filterId) => quickFilters.find((f) => f.id === filterId))
    .filter(isDefined);
  const filtersNotInOrder = quickFilters.filter(
    (f) => !quickFilterOrder.includes(f.id),
  );
  orderedFilters.push(...filtersNotInOrder);

  const onDragEnd = (result: DropResult) => {
    const { draggableId, destination } = result;
    if (!destination || !report) {
      return;
    }

    const itemsWithoutDropped = orderedFilters.filter(
      (f) => f.id !== draggableId,
    );

    const newOrder = [
      ...itemsWithoutDropped.slice(0, destination.index).map((f) => f.id),
      draggableId,
      ...itemsWithoutDropped
        .slice(destination.index, itemsWithoutDropped.length)
        .map((f) => f.id),
    ];

    const newReport: ReportType = {
      ...report,
      quickFilterOrder: newOrder,
    };

    STORE.getReportsRef({ accountId: selectedAccountId })
      .doc(report.id)
      .set(withoutNulls(newReport));
  };

  const deleteFilter = (f: LegacySavedFilter) => {
    if (!report) {
      return;
    }

    STORE.filterDefinitions
      .getLegacySavedFiltersRef({ accountId: selectedAccountId })
      .doc(f.id)
      .delete()
      .then(() => {
        const orderWithoutDeleted = quickFilterOrder.filter(
          (fid) => fid !== f.id,
        );
        const newReport: ReportType = {
          ...report,
          quickFilterOrder: orderWithoutDeleted,
        };

        STORE.getReportsRef({ accountId: selectedAccountId })
          .doc(report.id)
          .set(withoutNulls(newReport));
      });
  };

  const closeForm = () => {
    setIsCreatingNewFilter(false);
    setEditingFilter(undefined);
  };

  if (isCreatingNewFilter || editingFilter) {
    return (
      <NewReportQuickFilterForm quickFilter={editingFilter} close={closeForm} />
    );
  }

  return (
    <QuickFilters
      isCreatingNewFilter={isCreatingNewFilter}
      setIsCreatingNewFilter={setIsCreatingNewFilter}
      orderedFilters={orderedFilters}
      onDragEnd={onDragEnd}
      deleteFilter={deleteFilter}
      editFilter={setEditingFilter}
      close={close}
    />
  );
};

export default ReportQuickFiltersSettings;
